import React, { memo } from 'react';
import { styled } from '@slicknode/stylemapper';
import { useRouter } from 'next/router';
import { useGate } from 'statsig-react';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { useHeaderStore } from '@store/header';

import { BLUR_BACKGROUND_PATHS, FEATURE_GATES, NAV_LINKS } from '@utils/constants';
import { HeaderAnimatedText } from './AnimatedText';
import { DesktopHeader } from './Headers/Desktop';
import { MobileHeader } from './Headers/Mobile';
import { NewMobileHamburgerSheet } from './NavMenu/NewMobileHamburger';

const StyledHeader = styled('header', 'top-0 z-header mx-auto', {
  variants: {
    isBlurBackground: {
      true: 'absolute left-0 right-0 md:left-sidebar-md lt:left-sidebar-lt',
      false: 'relative',
    },
    isArtistApp: {},
    hasBanner: {
      true: '',
      false: '',
    },
    hideMobileNav: {
      true: 'hidden md:flex',
      false: ' md:flex',
    },
  },
  compoundVariants: [
    {
      isBlurBackground: true,
      hasBanner: true,
      className: 'md:top-12 lt:top-[58px]',
    },
  ],
});

export const Header = memo(function Header({
  hideMobileNav,
  showNewFeatureBanner,
}: {
  hideMobileNav?: boolean;
  showNewFeatureBanner: boolean;
}) {
  const { tablet } = MediaQuery.useContainer();
  const { isSearchOpen } = useHeaderStore();
  const { pathname } = useRouter();
  const { isAuthenticatedWithAPI } = useAuth();

  const isLandingPage = pathname === NAV_LINKS.Home;
  const isAboutPage = pathname === NAV_LINKS.About;

  const isBlurBackground = BLUR_BACKGROUND_PATHS.includes(pathname);
  const strokeColor: 'white' | 'black' = isBlurBackground ? 'white' : 'black';

  const { hasDismissedNewPostFeatureBanner } = useBackendUserPersistedData();

  const { value: openPostsEnabled } = useGate(FEATURE_GATES.OPEN_POSTS);
  const hasBanner =
    (hasDismissedNewPostFeatureBanner.type !== 'dismissed' || !isAuthenticatedWithAPI) &&
    openPostsEnabled &&
    showNewFeatureBanner;
  return (
    <>
      <StyledHeader
        isBlurBackground={isBlurBackground}
        hasBanner={hasBanner}
        hideMobileNav={hideMobileNav}
      >
        <div className="mx-auto w-full px-4 py-4 md:px-6 md:py-5 dt:max-w-content">
          <div className="hidden md:block">
            <DesktopHeader />
          </div>

          {isSearchOpen || hideMobileNav ? null : (
            <div className="md:hidden">
              <MobileHeader strokeColor={strokeColor} />
            </div>
          )}
          {isLandingPage || isAboutPage ? <HeaderAnimatedText /> : null}
        </div>
      </StyledHeader>

      {!tablet && !hideMobileNav && <NewMobileHamburgerSheet />}
    </>
  );
});
